/**
 * Shared dataviz tokens + geometry helpers for the Conversations dashboard charts.
 *
 * Light-mode only (the app theme is `mode: 'light'`), so the dark half of the
 * reference mockup (docs/plans/conversations/09-dashboard-mockup.html) is dropped
 * entirely — no CSS vars, no theme plumbing, plain constants.
 *
 * Rules these tokens encode (dataviz skill):
 *  - `series` is categorical and FIXED-ORDER; never cycled past slot 4.
 *  - `ramp` is a single-hue ordinal scale (5 steps) — use it for part-of-whole /
 *    ordered splits, never as a categorical palette.
 *  - status hues (`good`…`critical`) are never reused as a series colour.
 *  - text never wears the series colour: labels use `ink`/`ink2`/`muted`, except a
 *    label set INSIDE a coloured fill, which goes through `inkOn()`.
 */

import { useEffect, useRef, useState } from 'react';

export const VIZ = {
  series: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100'] as const,
  ramp: ['#86b6ef', '#5598e7', '#2a78d6', '#256abf', '#184f95'] as const,
  good: '#0ca30c',
  warning: '#fab219',
  serious: '#ec835a',
  critical: '#d03b3b',
  deltaUpGood: '#006300',
  grid: '#e1e0d9',
  axis: '#c3c2b7',
  ink: '#0b0b0b',
  ink2: '#52514e',
  muted: '#898781',
  surface: '#fcfcfb',
};

/** Horizontal bar path: DATA end rounded 4px, square at the baseline. */
export function hBarPath(x: number, y: number, w: number, h: number, r = 4): string {
  const rr = Math.max(0, Math.min(r, w, h / 2));
  if (w <= 0.5) return `M${x},${y}h0`;
  return `M${x},${y}h${w - rr}a${rr},${rr} 0 0 1 ${rr},${rr}v${h - 2 * rr}a${rr},${rr} 0 0 1 ${-rr},${rr}h${-(w - rr)}z`;
}

/** Relative luminance -> ink or white for a label SET INSIDE a coloured fill. */
export function inkOn(hex: string): string {
  const h = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4]
    .map((i) => parseInt(h.slice(i, i + 2), 16) / 255)
    .map((c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.42 ? VIZ.ink : '#ffffff';
}

/**
 * Ticks from 0 to the first "nice" step at or above `max`.
 * The LAST element is the axis maximum — callers scale against `ticks[ticks.length - 1]`.
 */
export function niceTicks(max: number, count = 4): number[] {
  if (!(max > 0) || !isFinite(max)) return [0];
  const raw = max / Math.max(1, count);
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? mag * 10;
  const out: number[] = [];
  for (let i = 0; i <= 64; i++) {
    const v = +(i * step).toFixed(6);
    out.push(v);
    if (v >= max - 1e-9) break;
  }
  return out;
}

/**
 * Number locale for every formatter below. Module-level because the formatters are
 * called from INSIDE the primitives (tooltips, tick labels, inline bar labels), where
 * threading it as a prop would mean a `locale` on all six components. The container
 * sets it once from `i18n.language`; French is the default, like the rest of the product.
 */
let vizLocale = 'fr-FR';

export function setVizLocale(lang: string | undefined) {
  vizLocale = lang?.toLowerCase().startsWith('en') ? 'en-US' : 'fr-FR';
}

/** `0.336` -> `33,6 %` (FR) / `33.6%` (EN). */
export function fmtPct(v: number, digits = 1): string {
  const n = (v * 100).toLocaleString(vizLocale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  // The no-break space before the % sign is a French typographic rule, not an English one.
  return vizLocale === 'fr-FR' ? `${n} %` : `${n}%`;
}

/** `12400` -> `12 400` (FR) / `12,400` (EN). */
export function fmtInt(n: number): string {
  return Math.round(n).toLocaleString(vizLocale);
}

/**
 * Signed delta -> arrow + magnitude + colour. Colour NEVER carries the meaning
 * alone: the arrow glyph is part of the returned text, and callers add the unit.
 * `v` is in the caller's own unit (points, counts) — a magnitude under 0.05 reads flat.
 */
export function fmtDelta(v: number, goodUp = true): { text: string; color: string } {
  const flat = !isFinite(v) || Math.abs(v) < 0.05;
  const arrow = flat ? '→' : v > 0 ? '↑' : '↓';
  const good = goodUp ? v > 0 : v < 0;
  const color = flat ? VIZ.muted : good ? VIZ.deltaUpGood : VIZ.critical;
  const mag = Math.abs(v).toLocaleString(vizLocale, { maximumFractionDigits: 1 });
  return { text: `${arrow} ${mag}`, color };
}

export const VIZ_FONT = 'system-ui, -apple-system, "Segoe UI", sans-serif';

let measureCtx: CanvasRenderingContext2D | null | undefined;

/**
 * Real text width, so a value label can be placed outside a bar or moved inside —
 * never clipped, never `overflow: hidden`. Falls back to a per-character estimate.
 */
export function measureText(text: string, fontSize = 11, weight = 400): number {
  if (measureCtx === undefined) {
    measureCtx =
      typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
  }
  if (!measureCtx) return text.length * fontSize * 0.55;
  measureCtx.font = `${weight} ${fontSize}px ${VIZ_FONT}`;
  return measureCtx.measureText(text).width;
}

/** Container width via ResizeObserver — each chart sizes to its card. */
export function useMeasuredWidth<T extends HTMLElement>(fallback = 640) {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    setWidth(node.clientWidth || fallback);
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setWidth(w);
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, [fallback]);
  return [ref, width] as const;
}
